import { Stack, Text, VStack } from "@chakra-ui/react";
import FacebookFeedPreview from "./AdPreview/FacebookFeedPreview";
import FacebookStoryPreview from "./AdPreview/FacebookStoryPreview";
import InstagramPostPreview from "./AdPreview/InstagramPostPreview";
import InstagramStoryPreview from "./AdPreview/InstagramStoryPreview";
import type { CreativeBlock } from "../guidedFlow/types";
import type { FacebookPage, InstagramAccount } from "../types";
import { resolveAdPreviewCollaboration } from "../utils/adPreviewCollaboration";
import {
  creativeBlockHasSquareAdSlot,
  creativeBlockHasVerticalAdSlot,
} from "../utils/creativeBlockAdPreviewSlots";

export type CreativeBlockAdPreviewPlatform = "facebook" | "instagram";

export type CreativeBlockAdPreviewsProps = {
  creative: CreativeBlock;
  pages: readonly FacebookPage[];
  instagramAccounts: readonly InstagramAccount[];
  ctaText: string;
  websiteUrl: string;
  eventName: string;
  eventDescription: string;
  fullWidth?: boolean;
  /** When set, only render previews for these platforms (edit-step tabs). */
  platforms?: readonly CreativeBlockAdPreviewPlatform[];
  partnerPostsEnabled?: boolean;
};

export default function CreativeBlockAdPreviews({
  creative,
  pages,
  instagramAccounts,
  ctaText,
  websiteUrl,
  eventName,
  eventDescription,
  fullWidth = true,
  platforms,
  partnerPostsEnabled = false,
}: CreativeBlockAdPreviewsProps) {
  const showSquare = creativeBlockHasSquareAdSlot(creative);
  const showVertical = creativeBlockHasVerticalAdSlot(creative);
  const hasInstagram = Boolean(creative.instagramAccount?.id);
  const showFacebook = !platforms || platforms.includes("facebook");
  const showInstagram = !platforms || platforms.includes("instagram");

  if (!showSquare && !showVertical) {
    return null;
  }

  const collaboration = partnerPostsEnabled
    ? resolveAdPreviewCollaboration(creative, pages, instagramAccounts)
    : undefined;

  const instagramPreviews =
    showInstagram && hasInstagram && (showSquare || showVertical) ? (
      <Stack spacing={4}>
        <Text fontSize="xs" fontWeight="semibold" color="gray.600">
          Instagram
        </Text>
        {showSquare && (
          <InstagramPostPreview
            username={creative.instagramAccount?.username ?? "Account"}
            message={creative.primaryText}
            ctaText={ctaText}
            mediaUrls={
              creative.squareMediaUrl ? [creative.squareMediaUrl] : []
            }
            profilePictureUrl={
              creative.instagramAccount?.profile_picture_url || undefined
            }
            collaboration={collaboration}
            mediaType="single"
            fullWidth={fullWidth}
          />
        )}
        {showVertical && (
          <InstagramStoryPreview
            username={creative.instagramAccount?.username ?? "Account"}
            ctaText={ctaText}
            mediaUrls={
              creative.verticalMediaUrl ? [creative.verticalMediaUrl] : []
            }
            profilePictureUrl={
              creative.instagramAccount?.profile_picture_url || undefined
            }
            collaboration={collaboration}
            mediaType="single"
            fullWidth={fullWidth}
          />
        )}
      </Stack>
    ) : null;

  const facebookPreviews =
    showFacebook && (showSquare || showVertical) ? (
      <Stack spacing={4}>
        <Text fontSize="xs" fontWeight="semibold" color="gray.600">
          Facebook
        </Text>
        {showSquare && (
          <FacebookFeedPreview
            message={creative.primaryText}
            ctaText={ctaText}
            mediaUrls={
              creative.squareMediaUrl ? [creative.squareMediaUrl] : []
            }
            mediaType="single"
            pageName={creative.fbPage?.name ?? "Account"}
            profilePictureUrl={creative.fbPage?.picture || undefined}
            collaboration={collaboration}
            websiteUrl={websiteUrl}
            eventName={eventName}
            description={eventDescription}
            fullWidth={fullWidth}
          />
        )}
        {showVertical && (
          <FacebookStoryPreview
            message={creative.primaryText}
            ctaText={ctaText}
            mediaUrls={
              creative.verticalMediaUrl ? [creative.verticalMediaUrl] : []
            }
            mediaType="single"
            pageName={creative.fbPage?.name ?? "Account"}
            profilePictureUrl={creative.fbPage?.picture || undefined}
            collaboration={collaboration}
            websiteUrl={websiteUrl}
            eventName={eventName}
            description={eventDescription}
            fullWidth={fullWidth}
          />
        )}
      </Stack>
    ) : null;

  if (!facebookPreviews && !instagramPreviews) {
    return null;
  }

  return (
    <VStack align="stretch" spacing={6} w="full">
      {facebookPreviews}
      {instagramPreviews}
    </VStack>
  );
}
